import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, User, ArrowRight, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { blogPosts, companyInfo } from "../data/mockData";

const BlogPost = () => {
  const { id } = useParams();
  const post = blogPosts.find((p) => p.id === id);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const related = blogPosts.filter((p) => p.id !== id).slice(0, 3);

  // Simple markdown-ish renderer for bold and paragraphs
  const renderContent = (content) => {
    return content.split("\n\n").map((paragraph, i) => {
      if (paragraph.trim() === "") return null;
      const parts = paragraph.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i} className="text-slate-700 text-lg leading-relaxed mb-6">
          {parts.map((part, j) => {
            if (part.startsWith("**") && part.endsWith("**")) {
              return (
                <strong key={j} className="font-serif text-slate-900 text-xl block mt-2 mb-2">
                  {part.slice(2, -2)}
                </strong>
              );
            }
            return <span key={j}>{part}</span>;
          })}
        </p>
      );
    });
  };

  return (
    <div data-testid="blog-post-page" className="bg-stone-50">
      {/* HEADER */}
      <section className="relative bg-amber-50/30 pt-12 lg:pt-16 pb-16 lg:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            data-testid="blog-post-back-link"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-amber-700 text-sm font-medium mb-10 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Resources
          </Link>

          <Badge className="bg-amber-700 hover:bg-amber-800 text-white rounded-full mb-6 px-4 py-1.5">
            {post.category}
          </Badge>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-tight tracking-tight mb-8">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-sm text-slate-600">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4 text-amber-700" />
              {post.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-amber-700" />
              {post.date}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-700" />
              {post.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* IMAGE */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <div className="aspect-[16/9] rounded-[2rem] overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-serif italic text-2xl text-slate-700 leading-relaxed mb-12 pb-12 border-b border-stone-200">
            {post.excerpt}
          </p>
          <div className="prose-content">{renderContent(post.content)}</div>

          {/* Share / CTA */}
          <div className="mt-16 pt-12 border-t border-stone-200">
            <div className="bg-amber-50/40 rounded-3xl p-8 lg:p-10 text-center">
              <p className="font-script text-3xl text-amber-700 mb-3">Need a hand?</p>
              <h3 className="font-serif text-2xl lg:text-3xl text-slate-900 mb-4">
                We're here whenever you're ready.
              </h3>
              <p className="text-slate-600 leading-relaxed mb-8 max-w-xl mx-auto">
                A short, friendly conversation is always free. We'll listen, share what we know, and help you find the right next step for your family.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href={`tel:${companyInfo.phone.replace(/\D/g, "")}`}>
                  <Button className="bg-amber-700 hover:bg-amber-800 text-white rounded-full px-7 py-5 w-full sm:w-auto">
                    <Phone className="w-4 h-4 mr-2" />
                    {companyInfo.phone}
                  </Button>
                </a>
                <Link to="/contact">
                  <Button
                    variant="outline"
                    className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-full px-7 py-5 w-full sm:w-auto"
                  >
                    Request a consultation
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-10 text-center">
            Continue reading
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {related.map((r) => (
              <Link
                key={r.id}
                to={`/blog/${r.id}`}
                className="group block"
                data-testid={`related-post-${r.id}`}
              >
                <div className="aspect-[4/3] rounded-2xl overflow-hidden mb-5">
                  <img
                    src={r.image}
                    alt={r.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <p className="text-xs text-amber-700 font-semibold uppercase tracking-widest mb-2">
                  {r.category}
                </p>
                <h3 className="font-serif text-xl text-slate-900 group-hover:text-amber-700 transition-colors leading-tight">
                  {r.title}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
